import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/stores/auth.store";
import { ValidationPanel } from "@/features/lines/editor/components/ValidationPanel";
import { useUnifiedDraftIssues } from "@/features/lines/editor/hooks/useUnifiedDraftIssues";
import { useStableIssueState } from "@/features/lines/editor/hooks/useStableIssueState";
import type { LineCanvas } from "@/types/canvas.types";
import type { IssueTarget } from "@/types/line-editor.types";
import { EditorAggregateContext } from "./helpers";
import { EmbeddedContext } from "./helpers";
import { EditModeView } from "./EditModeView";
import { CreationModeView } from "./CreationModeView";

export interface LineEditorUnifiedProps {
  mode: "edit" | "creation";
}

export function LineEditorUnified({ mode }: LineEditorUnifiedProps) {
  const me = useAuthStore((s) => s.user)!;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // O canvas COMO ESTÁ SENDO EDITADO, publicado de baixo pelo `ProductionLineCanvas`.
  //
  // O painel precisa validar o que está na tela, não o que está salvo: antes disso, arrastar uma
  // máquina para o canvas não mexia em nada, e o aviso "sem representação no canvas" continuava
  // com a máquina visivelmente lá até alguém salvar.
  //
  // `null` no primeiro render (o canvas ainda não montou) ⇒ vale o do servidor.
  //
  // ⚠️ CARIMBADO COM A LINHA. A rota `/lines/:id/edit` não tem `key`, então navegar de A para B
  // RE-RENDERIZA este componente em vez de remontá-lo — e o estado sobreviveria à troca. O painel
  // passaria a validar as máquinas de B contra o canvas de A (toda máquina de B "sem representação
  // no canvas"), e nada o corrigiria enquanto o rascunho de B não fosse semeado. Descartar por
  // comparação, e não num efeito, é o que faz a correção acontecer no MESMO render da navegação.
  const [edited, setEdited] = useState<{ lineId: string; canvas: LineCanvas } | null>(null);
  const publishCanvas = useCallback(
    (canvas: LineCanvas) => setEdited({ lineId: id ?? "", canvas }),
    [id]
  );
  const editedCanvas = edited && edited.lineId === (id ?? "") ? edited.canvas : null;
  const activeIssues = useUnifiedDraftIssues({ mode, lineId: id, editedCanvas });
  const aggregate = useStableIssueState(activeIssues);

  const onJumpTo = useCallback((target: IssueTarget) => {
    if (target.kind === "header") {
      window.dispatchEvent(
        new CustomEvent("opus:editor:focus-header", { detail: { field: target.field } })
      );
    } else if (target.kind === "machine") {
      window.dispatchEvent(
        new CustomEvent("opus:editor:focus-machine", {
          detail: { id: target.id, field: target.field },
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent("opus:editor:focus-node", { detail: { nodeId: target.id } })
      );
    }
  }, []);

  if (me.role !== "GESTOR") {
    return <Navigate to="/lines" replace />;
  }

  const titleText = mode === "creation" ? "Nova linha" : "Editar linha";
  const subtitleText =
    mode === "creation"
      ? "Configure cabeçalho, máquinas e fluxo. Nada é salvo até você clicar em Salvar."
      : "Estrutura da linha (somente GESTOR).";

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[24px] font-semibold tracking-tight text-text-primary">
            {titleText}
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">{subtitleText}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (mode === "creation" ? navigate("/lines") : navigate(-1))}
          >
            <ArrowLeft className="h-4 w-4" />
            Cancelar
          </Button>
          {}
        </div>
      </header>

      <div className="grid grid-cols-[260px_1fr] gap-4 max-md:grid-cols-1">
        <ValidationPanel
          issues={aggregate.displayed}
          pendingCount={aggregate.pendingCount}
          onJumpTo={onJumpTo}
        />

        <Card className="overflow-hidden p-4">
          {}
          <EmbeddedContext.Provider value={true}>
            <EditorAggregateContext.Provider value={aggregate}>
              {mode === "edit" ? (
                <EditModeView onEditCanvasChange={publishCanvas} />
              ) : (
                <CreationModeView />
              )}
            </EditorAggregateContext.Provider>
          </EmbeddedContext.Provider>
        </Card>
      </div>
    </section>
  );
}

export default LineEditorUnified;
